/**
 * Горячие клавиши в работе: один обработчик keydown на окно, пока оно в фокусе, и перехват
 * в фоне через приложение для компьютера — только для микрофона и звука и только в голосе:
 * вне разговора переключать их поверх других программ незачем.
 */
import { useEffect } from 'react'
import { desktop, type GlobalHotkey } from '@/lib/desktop'
import { HOTKEY_ACTIONS, isMouseCode, matchesEvent, matchesMouse, worksInInputs, type HotkeyAction } from '@/lib/hotkeys'
import { pttMouseButton } from '@/lib/audio/ptt'
import { consumePress, guardButton } from '@/lib/mousePress'
import { useAppStore, type View } from '@/store/app'
import { useHotkeyStore } from '@/store/hotkeys'
import { useVoiceStore } from '@/store/voice'
import { useDesktopOverlay } from './useDesktopOverlay'

/*
 * Действия, чьё сочетание на мыши сейчас ловит оболочка. Клавишу окно в фокусе получает само,
 * и оболочка молчит, пока фокус у нас. С мышью так не выходит: кнопку видит окно под курсором,
 * а не окно в фокусе, и по фокусу нажатие то терялось бы, то срабатывало дважды. Поэтому кнопки
 * мыши оболочка присылает всегда, а в окне их только гасим, не выполняя второй раз.
 */
let shellMouse: ReadonlySet<string> = new Set()

export function runHotkey(action: HotkeyAction): void {
  if (action === 'toggleMute') void useVoiceStore.getState().toggleMute()
  else if (action === 'toggleDeafen') void useVoiceStore.getState().toggleDeaf()
  else if (action === 'toggleOverlay') {
    if (useVoiceStore.getState().connection) void desktop?.overlay?.toggle().catch(() => undefined)
  }
  else {
    const st = useHotkeyStore.getState()
    st.setSwitcherOpen(!st.switcherOpen)
  }
}

function editing(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  return Boolean(el && typeof el.closest === 'function' && el.closest('input, textarea, select, [contenteditable="true"]'))
}

/** Ключ места для «недавних» быстрого перехода; друзья, каталог и пустой сервер не в счёт. */
function placeKey(view: View): string | null {
  if (view.kind === 'guild') return view.channelId ? `c:${view.channelId}` : null
  if (view.kind === 'home' && 'channelId' in view) return `d:${view.channelId}`
  return null
}

const GLOBAL = new Set<string>(HOTKEY_ACTIONS.filter((a) => a.global).map((a) => a.id))

export function useHotkeys(): void {
  useDesktopOverlay()
  // в окне: сочетание срабатывает один раз на нажатие; пока сочетание записывают — молчим
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat || e.isComposing) return
      const st = useHotkeyStore.getState()
      if (st.recording) return
      for (const { id } of HOTKEY_ACTIONS) {
        if (id === 'toggleOverlay' && (!desktop?.overlay || !useVoiceStore.getState().connection)) continue
        const combo = st.bindings[id]
        if (!combo || !matchesEvent(combo, e)) continue
        if (!worksInInputs(combo) && editing(e.target)) return
        e.preventDefault()
        runHotkey(id)
        return
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // кнопки мыши: на document в фазе захвата — запись сочетания и рации слушает window и успевает
  // забрать нажатие раньше, а кнопки интерфейса, которые останавливают событие, уже не мешают
  useEffect(() => {
    const onMouse = (e: MouseEvent) => {
      const st = useHotkeyStore.getState()
      if (st.recording) return
      for (const { id } of HOTKEY_ACTIONS) {
        if (id === 'toggleOverlay' && (!desktop?.overlay || !useVoiceStore.getState().connection)) continue
        const combo = st.bindings[id]
        if (!combo || !matchesMouse(combo, e)) continue
        consumePress(e)
        if (!shellMouse.has(id)) runHotkey(id)
        return
      }
    }
    document.addEventListener('mousedown', onMouse, true)
    return () => document.removeEventListener('mousedown', onMouse, true)
  }, [])

  // Рация на кнопке мыши: слушают её в разговоре (store/voice), а без действий браузера она всегда,
  // пока включён режим рации. Нажатая по привычке вне разговора, боковая иначе увела бы со страницы,
  // а средняя открыла бы ссылку — в приложении для компьютера это ещё и окно браузера.
  const pttKey = useVoiceStore((s) => (s.prefs.inputMode === 'ptt' ? s.prefs.pttKey : ''))
  useEffect(() => guardButton(pttMouseButton(pttKey)), [pttKey])

  // недавние места для быстрого перехода
  useEffect(() => {
    const remember = (view: View) => {
      const key = placeKey(view)
      if (key) useHotkeyStore.getState().rememberPlace(key)
    }
    remember(useAppStore.getState().view)
    return useAppStore.subscribe((s, prev) => {
      if (s.view !== prev.view) remember(s.view)
    })
  }, [])

  // в фоне — через оболочку; старая оболочка моста не знает, тогда только в окне
  const inVoice = useVoiceStore((s) => Boolean(s.connection))
  const bindings = useHotkeyStore((s) => s.bindings)
  useEffect(() => {
    const bridge = desktop?.hotkeys
    if (!bridge) return
    const list: GlobalHotkey[] = inVoice
      ? HOTKEY_ACTIONS.filter((a) => a.global && (a.id !== 'toggleOverlay' || desktop?.overlay)).flatMap((a) => {
          const c = bindings[a.id]
          return c ? [{ id: a.id, ...c }] : []
        })
      : []
    // старая оболочка кнопок мыши не знает — тогда они работают только в окне
    void bridge
      .set(list)
      .then((ok) => {
        shellMouse = ok && bridge.mouse ? new Set(list.filter((k) => isMouseCode(k.code)).map((k) => k.id)) : new Set()
      })
      .catch(() => {
        shellMouse = new Set()
      })
  }, [inVoice, bindings])

  useEffect(() => {
    const bridge = desktop?.hotkeys
    if (!bridge) return
    // клавиши оболочка шлёт, только когда окно не в фокусе: в фокусе нажатие уже поймал обработчик
    // выше; кнопки мыши — всегда (см. shellMouse)
    const off = bridge.onTrigger((id) => {
      if (GLOBAL.has(id) && !useHotkeyStore.getState().recording) runHotkey(id as HotkeyAction)
    })
    return () => {
      off()
      shellMouse = new Set()
      void bridge.set([]).catch(() => undefined)
    }
  }, [])
}
